import { useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, FolderOpen, PenLine, Quote, BarChart3, Image as ImageIcon, Settings as SettingsIcon, LogOut, ExternalLink, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const NAV = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard, id: "admin-nav-overview" },
  { to: "/admin/portfolio", label: "Portfolio", icon: FolderOpen, id: "admin-nav-portfolio" },
  { to: "/admin/blog", label: "Blog", icon: PenLine, id: "admin-nav-blog" },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote, id: "admin-nav-testimonials" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, id: "admin-nav-analytics" },
  { to: "/admin/media", label: "Media Library", icon: ImageIcon, id: "admin-nav-media" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, id: "admin-nav-settings" },
];

export default function AdminLayout() {
  useDocumentTitle("Admin — JARE");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setThemeState] = useState(() => localStorage.getItem("jare-admin-theme") || "dark");

  const setTheme = (t) => {
    localStorage.setItem("jare-admin-theme", t);
    setThemeState(t);
  };
  const light = theme === "light";

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]" data-testid="admin-loading">
        <span className="font-display animate-pulse text-2xl font-extrabold text-white/60">JARE</span>
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className={`min-h-screen ${light ? "admin-light bg-[#f4f3f1] text-neutral-900" : "bg-[#050505] text-white"}`} data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="glass-strong fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-white/10 p-6 md:flex">
        <Link to="/" className="font-display text-xl font-extrabold tracking-tight">
          JARE
          <span className="ml-2 rounded-full border border-white/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">CMS</span>
        </Link>
        <nav className="mt-10 flex-1 space-y-1.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                  isActive ? "bg-white/[0.08] text-white" : "text-neutral-400 hover:bg-white/[0.04] hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1.5 border-t border-white/10 pt-4">
          <button
            onClick={() => setTheme(light ? "dark" : "light")}
            data-testid="admin-theme-toggle"
            className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-neutral-500 transition-colors hover:text-white"
          >
            {light ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            {light ? "Dark mode" : "Light mode"}
          </button>
          <Link to="/" className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-neutral-500 transition-colors hover:text-white" data-testid="admin-view-site">
            <ExternalLink className="h-4 w-4" /> View site
          </Link>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-neutral-500 transition-colors hover:text-red-300"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="glass-strong sticky top-0 z-40 flex items-center gap-2 overflow-x-auto border-b border-white/10 px-4 py-3 md:hidden">
        <Link to="/" className="font-display mr-2 shrink-0 text-lg font-extrabold">JARE</Link>
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`mobile-${n.id}`}
            className={({ isActive }) =>
              `shrink-0 rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                isActive ? "bg-white text-black" : "text-neutral-400"
              }`
            }
          >
            {n.label}
          </NavLink>
        ))}
        <button
          onClick={() => setTheme(light ? "dark" : "light")}
          data-testid="mobile-admin-theme-toggle"
          aria-label="Toggle theme"
          className="shrink-0 rounded-full px-3 py-2 text-xs text-neutral-500"
        >
          {light ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
        </button>
        <button onClick={handleLogout} data-testid="mobile-admin-logout" className="shrink-0 rounded-full px-3 py-2 text-xs text-neutral-500">
          <LogOut className="h-4 w-4" />
        </button>
      </div>

      <main className="p-5 md:ml-64 md:p-10">
        <Outlet context={{ adminTheme: theme, setAdminTheme: setTheme }} />
      </main>
    </div>
  );
}
